/**
 * Claims the corpus has already made the case for.
 *
 * Two hundred and twenty-one findings sat in this database with nothing reading
 * them: a hundred and eighty-one adjudicated comparisons, seventeen of which
 * say a comparator treats teachers better than their own agreement does, and a
 * hundred and forty silence rows of which fifteen are genuine gaps. The route
 * from any of them to a claim was somebody remembering they existed.
 *
 * Two sections, in that order, and the order is the argument. A comparison
 * where the Award beats the Agreement is the stronger starting point, because
 * the Award is the floor this group actually falls to if bargaining goes badly
 * — "we are below the legal minimum on this" is a sentence that ends a
 * discussion. "The support staff have something we do not" is a fairness
 * argument, which is weaker and still worth making.
 *
 * Every row carries its evidence and a way to open it. A suggestion a
 * representative cannot trace back to the comparison it came from is a model's
 * opinion with a button beside it, and this workbench has spent a day finding
 * out what those are worth.
 */

import { useQuery } from '@tanstack/react-query';
import { api, type SuggestionList } from '../lib/api.js';

export function Suggestions({
  onOpenClause,
}: {
  onOpenClause: (number: string) => void;
}) {
  const suggestions = useQuery({ queryKey: ['suggestions'], queryFn: api.suggestions });
  const data: SuggestionList | undefined = suggestions.data;

  if (!data) return null;
  if (data.behind.length === 0 && data.gaps.length === 0) {
    return (
      <p className="measure text-[12.5px] leading-relaxed text-[var(--color-muted)]">
        Nothing left to suggest. Every comparison where an instrument beats ours, and every gap the
        silence pass found, is already attached to a claim.
      </p>
    );
  }

  return (
    <div className="flex flex-col gap-5">
      <p className="measure text-[12.5px] leading-relaxed text-[var(--color-muted)]">
        {data.totals.suggested} starting points, drawn from comparisons that have been read and
        from gaps the silence pass found. Nothing here is a claim yet.
        {data.totals.alreadyClaimed > 0 &&
          ` ${data.totals.alreadyClaimed} more are already attached to claims and are not shown.`}
      </p>

      {data.behind.length > 0 && (
        <section>
          <h3 className="label mb-1.5 text-[var(--color-defect)]">
            We are behind another instrument — {data.behind.length}
          </h3>
          <p className="measure mb-2 text-[12.5px] leading-relaxed text-[var(--color-muted)]">
            A comparison that has been read and settled says the other instrument treats teachers
            better here. Where that instrument is the Award, it is the floor we fall to.
          </p>
          <ul className="flex flex-col gap-2">
            {data.behind.map((item) => (
              <li key={item.alignmentId} className="card">
                <div className="row mb-1">
                  <button
                    type="button"
                    className="clause-number text-[11px] underline"
                    onClick={() => onOpenClause(item.clause.number)}
                  >
                    {item.clause.number}
                  </button>
                  <span className="text-[13px] font-medium">{item.clause.heading ?? ''}</span>
                  <span className="text-[12px] text-[var(--color-muted)]">
                    behind {item.against.instrument}
                    {item.against.number ? ` ${item.against.number}` : ''}
                  </span>
                  {/*
                    Not a badge of quality — a statement about how the finding
                    was arrived at. `reread` means the second reading changed
                    the pipeline's verdict and this is what survived, which is a
                    different kind of evidence from a verdict nobody disturbed.
                  */}
                  {item.reread && (
                    <span className="label text-[10px] text-[var(--color-muted)]">
                      changed on a second reading
                    </span>
                  )}
                </div>
                {item.why && (
                  <p className="measure text-[12.5px] leading-relaxed">{item.why}</p>
                )}
              </li>
            ))}
          </ul>
        </section>
      )}

      {data.gaps.length > 0 && (
        <section>
          <h3 className="label mb-1.5 text-[var(--color-behind)]">
            They have it and we do not — {data.gaps.length}
          </h3>
          <p className="measure mb-2 text-[12.5px] leading-relaxed text-[var(--color-muted)]">
            Provisions in a comparator with nothing corresponding in our agreement. The silence
            pass read a hundred and forty and found most of them already covered; these are the
            ones that are not.
          </p>
          <ul className="flex flex-col gap-2">
            {data.gaps.map((item) => (
              <li key={item.silenceId} className="card">
                <div className="row mb-1">
                  <span className="text-[13px] font-medium">{item.subject}</span>
                  <span className="text-[12px] text-[var(--color-muted)]">
                    {item.against.instrument} {item.against.number}
                  </span>
                </div>
                <p className="measure text-[12.5px] leading-relaxed">{item.why}</p>
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}
